import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import NavBar from "../landingPage/header/navbar";
import DottedLoader from "../DottedLoader/DottedLoader.jsx";
import { BLOGS_CONTROLLER } from "../../env";
import "./index.scss";
import Footer from "../landingPage/footer";
import { REGISTER_POSTHOG_API_KEY, POSTHOG_API_HOST } from "../../env";
import posthog from "posthog-js";

const BlogsPage = () => {
  const { id } = useParams();
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState({ title: "", description: "" });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let utmSource = urlParams.get('utm_source');
    const email = urlParams.get('email') || "";

    if (!utmSource) {
      utmSource = localStorage.getItem('utm_source') || 'direct';
    }

    const phInstance = posthog.init(REGISTER_POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      autocapture: false,
      name: "register",
      capture_pageview: false,
      disable_session_recording: true,
      loaded: (ph) => {
        if (utmSource) {
          ph.register({ utm_source: utmSource });
        }
      },
    });

    if (phInstance && id) {
      phInstance.capture('blog_post_visit', {
        location: window.location.pathname,
        blog_id: id,
        utm_source: utmSource,
        email: email
      });
    }
  }, [id]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentSegments, setContentSegments] = useState([]);
  const [styles, setStyles] = useState("");

  useEffect(() => {
    const fetchBlogContent = async () => {
      try {
        const response = await axios.post(BLOGS_CONTROLLER, {
          doc_id: id,
          action: "get_blog_by_id",
        });
        const { html_content, meta } = response.data.body;

        setContent(html_content);
        setMeta({
          title: meta.title,
          description: meta.description,
          metaTags: (meta.meta_tags?.length ? meta.meta_tags.join(",") : "")
        });
      } catch (err) {
        setError("Failed to load the blog content.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlogContent();
    } else {
      setError("Invalid blog ID.");
      setLoading(false);
    }
  }, [id]);

  // Parse content and split into segments
  useEffect(() => {
    if (!content) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    // Extract styles
    const extractedStyles = Array.from(doc.querySelectorAll('style'))
      .map(s => s.textContent)
      .join('\n');
    setStyles(extractedStyles);

    // Get body content
    const bodyHtml = doc.body ? doc.body.innerHTML : content;

    // Find Instagram blockquotes using regex to preserve exact HTML
    const instagramRegex = /<blockquote[^>]*class="[^"]*instagram-media[^"]*"[^>]*>[\s\S]*?<\/blockquote>/gi;
    const instagramMatches = bodyHtml.match(instagramRegex) || [];

    if (instagramMatches.length === 0) {
      // No Instagram embeds - just one segment
      setContentSegments([{ type: 'content', html: bodyHtml }]);
      return;
    }

    // Split content by Instagram embeds
    const segments = [];
    let remainingHtml = bodyHtml;

    instagramMatches.forEach((instagramHtml, index) => {
      const splitIndex = remainingHtml.indexOf(instagramHtml);

      if (splitIndex > 0) {
        // Add content before this Instagram embed
        const beforeContent = remainingHtml.substring(0, splitIndex);
        if (beforeContent.trim()) {
          segments.push({ type: 'content', html: beforeContent });
        }
      }

      // Add Instagram embed
      segments.push({ type: 'instagram', html: instagramHtml });

      // Update remaining HTML
      remainingHtml = remainingHtml.substring(splitIndex + instagramHtml.length);
    });

    // Add any remaining content after the last Instagram embed
    if (remainingHtml.trim()) {
      segments.push({ type: 'content', html: remainingHtml });
    }

    setContentSegments(segments);
  }, [content]);

  // Render Shadow DOM for content segments
  const contentRefCallback = useCallback((node) => {
    if (node && !node.shadowRoot && node.dataset.html) {
      try {
        const shadowRoot = node.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = `
          <style>
            ${styles}
            :host {
              display: block;
            }
            img {
              max-width: 100%;
              width: 100%;
              height: auto;
              object-fit: contain;
            }
            iframe {
              max-width: 100%;
            }
          </style>
          ${node.dataset.html}
        `;
        // Clear the data attribute after use
        delete node.dataset.html;

        // Make spans containing images responsive
        shadowRoot.querySelectorAll('span').forEach(span => {
          if (span.querySelector('img')) {
            span.style.width = '100%';
            span.style.display = 'block';
          }
        });

        // Make images responsive with lazy loading
        shadowRoot.querySelectorAll('img').forEach(img => {
          img.style.width = '100%';
          img.style.objectFit = 'contain';
          img.setAttribute('loading', 'lazy');
        });
      } catch (err) {
        console.error('Error creating Shadow DOM:', err);
        node.innerHTML = node.dataset.html;
      }
    }
  }, [styles]);

  // Process Instagram embeds after render
  useEffect(() => {
    if (contentSegments.length === 0) return;

    const hasInstagram = contentSegments.some(s => s.type === 'instagram');
    if (!hasInstagram) return;

    const processEmbeds = () => {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (window.instgrm) {
        processEmbeds();
      } else {
        const scriptUrl = "https://www.instagram.com/embed.js";
        if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
          const script = document.createElement("script");
          script.src = scriptUrl;
          script.async = true;
          script.onload = () => setTimeout(processEmbeds, 100);
          document.body.appendChild(script);
        } else {
          setTimeout(processEmbeds, 500);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [contentSegments]);

  if (loading) {
    return (
      <div className="loader">
        <NavBar />
        <DottedLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <NavBar />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.metaTags} />
      </Helmet>
      <NavBar />
      <div className="blog-container">
        {contentSegments.map((segment, index) => {
          if (segment.type === 'content') {
            return (
              <div
                key={`content-${index}`}
                ref={contentRefCallback}
                data-html={segment.html}
                className="blog-content-section"
              />
            );
          } else if (segment.type === 'instagram') {
            return (
              <div
                key={`instagram-${index}`}
                className="instagram-embed-wrapper"
                dangerouslySetInnerHTML={{ __html: segment.html }}
              />
            );
          }
          return null;
        })}
      </div>
      <Footer />
    </div>
  );
};

export default BlogsPage;
